import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Landmark, Plus, Pencil, Trash2, HandCoins } from 'lucide-react';
import { DebtPayoffChart } from './DebtPayoffChart';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(n || 0);
export const DEBT_TYPES = { bank_loan: 'Bank loan', overdraft: 'Overdraft', supplier: 'Supplier', vehicle_finance: 'Vehicle finance', jsm_trading: 'JSM Trading', sars: 'SARS', other: 'Other' };
const EMPTY = { creditor: '', debt_type: 'bank_loan', original_amount: '', balance: '', monthly_payment: '', interest_rate: '', due_date: '', notes: '' };

const DebtDialog = ({ open, onClose, initial, onSave }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF({ ...EMPTY, ...(initial || {}) }); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const num = (k, label) => (
    <div><Label className="text-xs">{label}</Label><Input type="number" min="0" value={f[k]} onChange={set(k)} className="bg-slate-700 border-slate-600" data-testid={`debt-${k}-input`} /></div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-800 text-white border-slate-600 max-w-lg" data-testid="debt-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? 'Edit debt' : 'Add debt'}</DialogTitle><DialogDescription className="text-slate-400">Anything the business owes.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs">Creditor *</Label><Input value={f.creditor} onChange={set('creditor')} className="bg-slate-700 border-slate-600" data-testid="debt-creditor-input" /></div>
          <div><Label className="text-xs">Type</Label>
            <select value={f.debt_type} onChange={set('debt_type')} className="w-full h-9 rounded-md bg-slate-700 border border-slate-600 px-2 text-sm" data-testid="debt-type-select">
              {Object.entries(DEBT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><Label className="text-xs">Due / end date</Label><Input type="date" value={f.due_date} onChange={set('due_date')} className="bg-slate-700 border-slate-600" data-testid="debt-due-date-input" /></div>
          {num('original_amount', 'Original amount (R)')}{num('balance', 'Balance owed (R) *')}
          {num('monthly_payment', 'Monthly payment (R)')}{num('interest_rate', 'Interest % per year')}
          <div className="col-span-2"><Label className="text-xs">Notes</Label><Input value={f.notes} onChange={set('notes')} className="bg-slate-700 border-slate-600" data-testid="debt-notes-input" /></div>
        </div>
        <Button className="w-full bg-rose-600 hover:bg-rose-700" onClick={() => onSave(f)} data-testid="debt-save-btn">Save</Button>
      </DialogContent>
    </Dialog>
  );
};

const PaymentDialog = ({ debt, onClose, onSave }) => {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => { setAmount(debt ? String(debt.monthly_payment || '') : ''); setNote(''); }, [debt]);
  return (
    <Dialog open={!!debt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-800 text-white border-slate-600 max-w-sm" data-testid="debt-payment-dialog">
        <DialogHeader><DialogTitle>Record payment — {debt?.creditor}</DialogTitle><DialogDescription className="text-slate-400">Balance now {R(debt?.balance)}</DialogDescription></DialogHeader>
        <Input type="number" min="0" placeholder="Amount (R)" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-slate-700 border-slate-600" data-testid="debt-payment-amount" />
        <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="bg-slate-700 border-slate-600" data-testid="debt-payment-note" />
        <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => onSave(amount, note)} data-testid="debt-payment-save">Save payment</Button>
      </DialogContent>
    </Dialog>
  );
};

export const DebtRegister = ({ headers }) => {
  const [data, setData] = useState({ debts: [], summary: {} });
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API_URL}/api/debts`, { headers })).data); }
    catch { toast.error('Could not load debts'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    const body = { ...f };
    ['original_amount', 'balance', 'monthly_payment', 'interest_rate'].forEach((k) => { body[k] = Number(body[k] || 0); });
    try {
      if (editing?.id) await axios.put(`${API_URL}/api/debts/${editing.id}`, body, { headers });
      else await axios.post(`${API_URL}/api/debts`, body, { headers });
      setEditing(null); load(); toast.success('Debt saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const pay = async (amount, note) => {
    try { await axios.post(`${API_URL}/api/debts/${paying.id}/payments`, { amount: Number(amount), note }, { headers }); setPaying(null); load(); toast.success('Payment recorded'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Payment failed'); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.creditor}?`)) return;
    await axios.delete(`${API_URL}/api/debts/${d.id}`, { headers }); load();
  };
  const s = data.summary || {};

  return (
    <Card className="bg-slate-800 border-rose-700/60" data-testid="debt-register-card">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
        <CardTitle className="text-white flex items-center gap-2 text-base"><Landmark className="h-5 w-5 text-rose-400" /> Debt Register</CardTitle>
        <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => setEditing({})} data-testid="add-debt-btn"><Plus className="h-3 w-3 mr-1" /> Add Debt</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div><p className="text-xs text-white font-bold">TOTAL DEBT</p><p className="text-rose-400 font-black text-xl" data-testid="debt-total">{R(s.total_owed)}</p></div>
          <div><p className="text-xs text-white font-bold">MONTHLY PAYMENTS</p><p className="text-orange-300 font-black text-xl" data-testid="debt-monthly">{R(s.monthly_payments)}</p></div>
          <div><p className="text-xs text-white font-bold">EST. INTEREST / MONTH</p><p className="text-amber-300 font-black text-xl" data-testid="debt-interest">{R(s.monthly_interest_estimate)}</p></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-white">
            <thead><tr className="text-xs text-slate-400 border-b border-slate-600">
              <th className="text-left py-2">Creditor</th><th className="text-left">Type</th><th className="text-right">Balance</th><th className="text-right">Monthly</th><th className="text-right">Interest</th><th className="text-left pl-3">Due</th><th />
            </tr></thead>
            <tbody>
              {data.debts.map((d) => (
                <tr key={d.id} className={`border-b border-slate-700 ${d.status === 'settled' ? 'opacity-50' : ''}`} data-testid={`debt-row-${d.id}`}>
                  <td className="py-2 font-semibold">{d.creditor}{d.notes && <div className="text-[11px] text-slate-400 font-normal">{d.notes}</div>}</td>
                  <td className="text-slate-300">{DEBT_TYPES[d.debt_type] || d.debt_type}</td>
                  <td className="text-right font-bold text-rose-300">{d.status === 'settled' ? 'Settled' : R(d.balance)}</td>
                  <td className="text-right">{R(d.monthly_payment)}</td>
                  <td className="text-right">{d.interest_rate ? `${d.interest_rate}%` : '—'}</td>
                  <td className="pl-3 text-slate-300">{d.due_date || '—'}</td>
                  <td className="text-right whitespace-nowrap">
                    <button className="p-1 hover:text-emerald-400" title="Record payment" onClick={() => setPaying(d)} data-testid={`debt-pay-${d.id}`}><HandCoins className="h-4 w-4" /></button>
                    <button className="p-1 hover:text-blue-400" title="Edit" onClick={() => setEditing(d)} data-testid={`debt-edit-${d.id}`}><Pencil className="h-4 w-4" /></button>
                    <button className="p-1 hover:text-rose-400" title="Delete" onClick={() => remove(d)} data-testid={`debt-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.debts.length === 0 && <p className="text-center text-slate-400 text-sm py-4">No debts captured yet. Add every loan, overdraft, supplier account and finance agreement.</p>}
        </div>
        <DebtPayoffChart headers={headers} refreshKey={data} />
      </CardContent>
      <DebtDialog open={editing !== null} initial={editing} onClose={() => setEditing(null)} onSave={save} />
      <PaymentDialog debt={paying} onClose={() => setPaying(null)} onSave={pay} />
    </Card>
  );
};
